import { BriefcaseIcon, CalendarIcon, UserIcon } from '@heroicons/react/24/outline';
import { atom, useAtomValue } from 'jotai';
import { Avatar } from 'flowbite-react';
import { useTranslation } from 'next-i18next';

import { WorkerResearchInfo } from '@restworld/utility-types';
import { PresentationCard } from '@restworld/ui-components';
import { AssociationBadge } from '@restworld/ui-components';
import { cn, ClipIcon, partnerLandingUrl } from '@restworld/utils-common';
import { Heading, Text } from '@restworld/ui-ds-base';
import { toDateFormat, useScroll } from '@restworld/utils-common';

import { workerProfileAtom, workerProfessionalAssociationsAtom } from '../global-state.worker-data';
import { WorkerDataLoader } from './worker-data-loader';
import { UpdateResearchStatusModal } from './update-research-status-modal';
import { UploadWorkerCv } from './upload-worker-cv';
import { DownloadCvButton } from './download-cv-button';
import { DeleteWorkerCv } from './delete-worker-cv-modal';
import { StickyJobDetailWrapper } from '../job-detail-block';
import { useFetchWorkerDataLoading } from '../../hooks';

const fullNameAtom = atom((get) => {
  const personalDetails = get(workerProfileAtom);
  return personalDetails?.name || personalDetails?.surname
    ? `${personalDetails?.name || ''} ${personalDetails?.surname || ''}`
    : undefined;
});

const lastUpdatedAtAtom = atom((get) => {
  const personalDetails = get(workerProfileAtom);
  return personalDetails?.research_status_last_updated
    ? toDateFormat(personalDetails.research_status_last_updated, 'dd/MM/yyyy')
    : undefined;
});

const workerOccupations = atom((get) => {
  const personalDetails = get(workerProfileAtom);
  const workerResearchInfo = personalDetails?.research_info as unknown as WorkerResearchInfo;
  const occupations =
    workerResearchInfo?.occupations
      ?.map((occupation) => occupation?.label_it?.label)
      .filter((occupation) => occupation) || [];
  return occupations.length > 0 ? occupations.join(', ') : workerResearchInfo?.domain_interest_var?.label;
});

export const UserProfileDetails = () => {
  const { isScrolled } = useScroll();
  const workerProfile = useAtomValue(workerProfileAtom);
  const fullName = useAtomValue(fullNameAtom);
  const lastUpdatedAt = useAtomValue(lastUpdatedAtAtom);
  const occupations = useAtomValue(workerOccupations);
  const professionalAssociations = useAtomValue(workerProfessionalAssociationsAtom);
  const workerDataLoading = useFetchWorkerDataLoading();
  const cv = workerProfile?.cv;
  const cvName = `${workerProfile?.public_cv_username || '[filename]'}.pdf`;
  function formatFileName(fileName) {
    const maxLength = 20;

    if (fileName.length > maxLength) {
      const firstPart = fileName.slice(0, 10);
      const lastPart = fileName.slice(-6);
      return `${firstPart}....${lastPart}`;
    }

    return fileName;
  }
  const { t } = useTranslation();
  return (
    <StickyJobDetailWrapper
      id="user-info-el"
      isScrolled={isScrolled}
      showRestaurant={false}
      bgColor="primary"
      className={'pt-[62px] lg:sticky md:max-w-sm relative'}
    >
      {workerDataLoading && <WorkerDataLoader />}
      {!workerDataLoading && (
        <>
          <CardWrapper>
            <ImageWrapper>
              <Avatar alt="avatar-img" img={workerProfile?.picture} size="lg" rounded color="gray" />
            </ImageWrapper>
            <Heading size={3} className="text-center">
              {fullName}
            </Heading>
            {/* Next to the identity rather than down with the preferences: this is
                who the person is, and at the bottom of the page it goes unread. */}
            {professionalAssociations.length > 0 && (
              <div className="flex flex-row flex-wrap justify-center gap-2 mt-2">
                {professionalAssociations.map((variable) => (
                  <AssociationBadge
                    key={variable.id}
                    label={variable.label}
                    description={variable.description}
                    iconUrl={variable.icon}
                    linkUrl={partnerLandingUrl(variable.key)}
                    linkLabel={t('common:find_out_more')}
                  />
                ))}
              </div>
            )}
            <Text size={2} className="mb-6 mt-2 text-center">
              {workerProfile?.email}
            </Text>
            <div className="flex flex-col gap-4 w-full">
              <PresentationCard
                variant="center"
                label={t('onboarding-page:desired_positions')}
                header={occupations}
                icon={<UserIcon className="w-6 h-6" />}
              />
              <PresentationCard
                variant="center"
                label={t('onboarding-page:work_status')}
                header={workerProfile?.research_status?.label}
                icon={<BriefcaseIcon className="w-6 h-6" />}
              />
            </div>
          </CardWrapper>
          <div className="flex flex-row items-center box-border justify-center w-full gap-3.75 mt-6 lg:pb-4 pb-0 pt-0 lg:mb-4 mb-6 lg:border-b border-b-0 border-primary-300 ">
            <UpdateResearchStatusModal />
          </div>
          <div className="flex flex-col items-center justify-center box-border w-full gap-2">
            <div className="self-start">
              <Heading size={5} color="white" weight="bold">
                {t('onboarding-page:your_cv')}
              </Heading>
            </div>
            {!cv && <UploadWorkerCv />}
            {cv && (
              <div className="flex flex-row justify-between items-center w-full">
                <span className="flex flex-row items-center gap-1.5">
                  <ClipIcon />
                  <Heading size={5} color="white" weight="normal">
                    {formatFileName(cvName)}
                  </Heading>
                </span>
                <div className="inline-flex flex-row  items-center gap-2">
                  <DownloadCvButton {...{ url: cv, name: cvName }} />
                  <DeleteWorkerCv />
                </div>
              </div>
            )}
          </div>
          <CalendarWrapper uploaded={!!cv}>
            <div className="inline-flex flex-row gap-1 items-center">
              <CalendarIcon className="w-[18px] h-[18px] text-white" />
              <Text size={2} color="white">
                {t('common:last_update_on')} {lastUpdatedAt}
              </Text>
            </div>
          </CalendarWrapper>
        </>
      )}
    </StickyJobDetailWrapper>
  );
};

export const CardWrapper = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <div
    className={cn('relative flex flex-col items-center w-full  px-6 pb-6 pt-14 bg-white rounded-3.75', className)}
    {...props}
  />
);

export const ImageWrapper = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <div
    className={cn(
      'absolute -top-10 flex flex-col items-center justify-center self-center w-fit h-fit rounded-full bg-white',
      className
    )}
    {...props}
  />
);

export const CalendarWrapper = ({
  uploaded,
  className,
  ...props
}: { uploaded: boolean } & React.HTMLAttributes<HTMLDivElement>) => (
  <div className={cn('flex flex-col w-full items-center', uploaded ? 'lg:mt-4 mt-7' : 'mt-4', className)} {...props} />
);
